import React, { useMemo, useState } from "react"
import { Alert, Button, Input, Select } from "antd"

import {
  addValue,
  recodeValue,
  removeRowsWithVariable,
  renameVariable,
  sameCode,
  updateRow,
} from "features/DataImport/components/DatamapEditor/utils/datamapEdits"
import { MAX_CODE } from "features/DataImport/components/DatamapEditor/utils/validateDatamap"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { DatamapCheckReport, DatamapDoc, DatamapIssue, UnmappedColumnFinding } from "types/datamap.types"

interface DatamapFindingsProps {
  doc: DatamapDoc
  issues: DatamapIssue[]
  /** The check report less what the edits have fixed (pendingFindings), or null before or without a check. */
  findings: DatamapCheckReport | null
  checkNote: string | null
  /** The datamap has been edited since the check the findings come from. */
  checkIsStale?: boolean
  onChange: (doc: DatamapDoc) => void
  /** Filters the grid to the row with this label. */
  onShowRow: (label: string) => void
  /** Opens the add-variable wizard on this data column. */
  onAddVariable: (column: string) => void
}

const TONE_CLASSES = {
  error: "border-rose-200 bg-rose-50",
  warning: "border-amber-200 bg-amber-50",
  neutral: "border-slate-200 bg-slate-50",
} as const

const Section: React.FC<{
  title: string
  tone: keyof typeof TONE_CLASSES
  hint?: string
  /** Sits on the title's line, such as a toggle that shows the list. */
  action?: React.ReactNode
  children?: React.ReactNode
}> = ({ title, tone, hint, action, children }) => (
  <section className={`rounded border px-3 py-2 ${TONE_CLASSES[tone]}`}>
    <div className="flex items-center gap-2">
      <p className="m-0 text-sm font-medium text-slate-800">{title}</p>
      {action}
    </div>
    {hint && <p className="m-0 text-xs text-slate-600">{hint}</p>}
    {children && <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0 text-sm">{children}</ul>}
  </section>
)

const plural = (n: number, word: string): string => `${n} ${word}${n === 1 ? "" : "s"}`

/** The check stops counting a column's different values here. */
const DISTINCT_CAP = 1000

/** A code a category row can list: a whole number from 0 to MAX_CODE. */
const isListableCode = (code: string): boolean => /^\d+$/.test(code) && Number(code) <= MAX_CODE

/** The most characters of a data value a column's summary shows; a longer one is cut short with "…". */
const SAMPLE_VALUE_CHARS = 24

/** `text`, cut to `max` characters with "…" when it is longer. */
const shorten = (text: string, max: number): string => {
  const chars = Array.from(text)
  return chars.length > max ? `${chars.slice(0, max).join("").trimEnd()}…` : text
}

/**
 * "3 different values: 1, 2, 3" or "empty": what a data column holds, from the check. Each value is
 * cut to `maxValueChars` characters, so a column of long answers still fits on a line.
 */
export const columnSummary = (finding: UnmappedColumnFinding, maxValueChars = SAMPLE_VALUE_CHARS): string => {
  if (finding.kind === "empty") return "empty"
  const values = finding.values
    .slice(0, 5)
    .map((value) => shorten(value.value, maxValueChars))
    .join(", ")
  // `more` only means the list of values was cut short; the count itself is exact below the cap.
  const count =
    finding.distinct >= DISTINCT_CAP ? `${finding.distinct}+ different values` : plural(finding.distinct, "different value")
  return `${count}: ${values}${finding.distinct > 5 || finding.more ? ", ..." : ""}`
}

/** What would go wrong with this datamap, and a fix for each finding. */
const DatamapFindings: React.FC<DatamapFindingsProps> = ({
  doc,
  issues,
  findings,
  checkNote,
  checkIsStale = false,
  onChange,
  onShowRow,
  onAddVariable,
}) => {
  // Typed labels for unknown codes, keyed by variable and code so a label edit elsewhere keeps them.
  const [newLabels, setNewLabels] = useState<Record<string, string>>({})
  // The data columns no row reads take one line until asked for: a platform can add dozens.
  const [showColumns, setShowColumns] = useState(false)
  const rowsById = useMemo(() => new Map(doc.rows.map((row) => [row.id, row])), [doc.rows])
  // The rows a finding concerns, found the way pendingFindings finds them: by variable, not label.
  const catRowOf = (variable: string) => doc.rows.find((row) => row.type === "CAT" && row.variable === variable)
  // The labels the rows reading a variable have now; the check's own list goes stale on a relabel.
  const labelsOf = (variable: string) => doc.rows.filter((row) => row.variable === variable).map((row) => row.label)
  const missingVariables = findings?.missingColumns.map((finding) => finding.variable) ?? []
  const unmappedColumns = findings?.unmappedColumns.map((finding) => finding.column) ?? []
  // A missing variable more than one column points to is probably split across them (Q5r1..Q5r3 for Q5).
  const linkCounts = new Map<string, number>()
  for (const finding of findings?.unmappedColumns ?? [])
    if (finding.linkTo) linkCounts.set(finding.linkTo, (linkCounts.get(finding.linkTo) ?? 0) + 1)
  // Unmapped columns never stop an import: platforms add columns of their own.
  const nothingBlocks =
    findings !== null &&
    issues.length === 0 &&
    findings.missingColumns.length + findings.unknownCodes.length + findings.badCells.length === 0
  const leftOutColumns = findings?.unmappedColumns.length ?? 0
  const leftOutNote =
    leftOutColumns === 1
      ? "1 data column is not in the datamap and is left out."
      : `${leftOutColumns} data columns are not in the datamap and are left out.`

  return (
    <div className="flex flex-col gap-3">
      {issues.length > 0 && (
        <Section title={`Fix before saving (${issues.length})`} tone="error">
          {issues.map((issue, index) => {
            const row = rowsById.get(issue.rowId)
            return (
              <li key={`${issue.rowId}-${index}`} className="flex items-center gap-2">
                <span>
                  <span className="font-medium">{row?.label || "(no label)"}</span>: {issue.message}
                </span>
                {row && row.label !== "" && (
                  <Button size="small" type="link" onClick={() => onShowRow(row.label)}>
                    Show
                  </Button>
                )}
              </li>
            )
          })}
        </Section>
      )}

      {checkNote && <Alert type="info" showIcon message={checkNote} />}

      {findings && findings.unknownCodes.length > 0 && (
        <Section
          title={`Codes in the data the datamap does not list (${findings.unknownCodes.reduce((n, f) => n + f.codes.length, 0)})`}
          tone="error"
          hint="Until each of these codes has a label, the whole question is left out of the import."
        >
          {findings.unknownCodes.flatMap((finding) => {
            const row = catRowOf(finding.variable)
            // Answers whose code the data never uses: likely the same answer under another code.
            const candidates = row
              ? row.values.filter(
                  (value) => value.code !== "" && value.label !== "" && !finding.known.some((code) => sameCode(code, value.code))
                )
              : []
            const items = finding.codes.map((code) => {
              const key = `${finding.variable}|${code.code}`
              // Negative or too large: no label can fix it, the row has to stop being a category.
              if (!isListableCode(code.code)) {
                return (
                  <li key={key} className="flex flex-wrap items-center gap-2">
                    <span>
                      <span className="font-medium">{finding.label}</span>: code {code.code} ({plural(code.count, "answer")})
                      {` can't be a category code (0 to ${MAX_CODE})`}
                    </span>
                    {row && (
                      <>
                        <DataQButton size="small" type="secondary" onClick={() => onChange(updateRow(doc, row.id, { type: "INT" }))}>
                          Make it INT
                        </DataQButton>
                        <DataQButton size="small" type="secondary" onClick={() => onChange(updateRow(doc, row.id, { type: "TEXT" }))}>
                          Make it TEXT
                        </DataQButton>
                      </>
                    )}
                  </li>
                )
              }
              const typed = (newLabels[key] ?? "").trim()
              return (
                <li key={key} className="flex flex-wrap items-center gap-2">
                  <span>
                    <span className="font-medium">{finding.label}</span>: code {code.code} ({plural(code.count, "answer")})
                  </span>
                  {row && candidates.length > 0 && <span className="text-xs text-slate-500">means</span>}
                  {row &&
                    candidates.map((value) => (
                      <DataQButton
                        key={`${value.code}|${value.label}`}
                        size="small"
                        type="secondary"
                        onClick={() => onChange(recodeValue(doc, row.id, value.code, code.code))}
                      >
                        {value.label}
                      </DataQButton>
                    ))}
                  {row && (
                    <>
                      <Input
                        size="small"
                        className="w-40"
                        placeholder="or a new label"
                        aria-label={`New label for code ${code.code} in ${finding.label}`}
                        value={newLabels[key] ?? ""}
                        onChange={(e) => setNewLabels({ ...newLabels, [key]: e.target.value })}
                      />
                      <DataQButton
                        size="small"
                        type="secondary"
                        isDisabled={typed === ""}
                        onClick={() => onChange(addValue(doc, row.id, code.code, typed))}
                      >
                        Add
                      </DataQButton>
                    </>
                  )}
                </li>
              )
            })
            return finding.more
              ? [
                  ...items,
                  <li key={`${finding.variable}#more`} className="text-xs text-slate-500">
                    There may be more codes than listed; check again after fixing these.
                  </li>,
                ]
              : items
          })}
        </Section>
      )}

      {findings && findings.unmappedColumns.length > 0 && (
        <Section
          title={leftOutNote}
          tone="neutral"
          hint={showColumns ? "Platforms add some columns of their own; leave those out." : undefined}
          action={
            <Button
              size="small"
              type="link"
              aria-expanded={showColumns}
              aria-label={showColumns ? "Hide data columns" : "Show data columns"}
              onClick={() => setShowColumns(!showColumns)}
            >
              {showColumns ? "Hide" : "Show"}
            </Button>
          }
        >
          {showColumns &&
            findings.unmappedColumns.map((finding) => {
              const linkTo = finding.linkTo
              const near = finding.near ? `; next to ${finding.near}` : ""
              // One line each: long sample values are cut short here and given in full on hover.
              return (
                <li key={finding.column} className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate" title={`${finding.column}: ${columnSummary(finding, Infinity)}${near}`}>
                    <span className="font-medium">{finding.column}</span>{" "}
                    <span className="text-xs text-slate-500">
                      {columnSummary(finding)}
                      {near}
                    </span>
                  </span>
                  {linkTo && missingVariables.includes(linkTo) && (linkCounts.get(linkTo) ?? 0) > 1 && (
                    <span className="shrink-0 text-xs text-slate-500">{linkTo} may be split across these columns</span>
                  )}
                  {linkTo && missingVariables.includes(linkTo) && (linkCounts.get(linkTo) ?? 0) === 1 && (
                    <DataQButton size="small" type="secondary" onClick={() => onChange(renameVariable(doc, linkTo, finding.column))}>
                      Use for {linkTo}
                    </DataQButton>
                  )}
                  {missingVariables.length > 0 && (
                    <Select
                      size="small"
                      className="w-52 shrink-0"
                      placeholder="Use for another row"
                      aria-label={`Use ${finding.column} for`}
                      value={null}
                      options={missingVariables.map((variable) => ({ value: variable, label: variable }))}
                      onChange={(variable: string) => onChange(renameVariable(doc, variable, finding.column))}
                    />
                  )}
                  <DataQButton size="small" type="secondary" onClick={() => onAddVariable(finding.column)}>
                    Add variable
                  </DataQButton>
                </li>
              )
            })}
        </Section>
      )}

      {findings && findings.missingColumns.length > 0 && (
        <Section title={`Rows with no data column (${findings.missingColumns.length})`} tone="warning" hint="These rows import nothing.">
          {findings.missingColumns.map((finding) => (
            <li key={finding.variable} className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{finding.variable}</span>
              <span className="text-xs text-slate-500">{labelsOf(finding.variable).join(", ")}</span>
              {unmappedColumns.length > 0 && (
                <Select
                  size="small"
                  className="w-52"
                  placeholder="Rename to a data column"
                  aria-label={`Rename ${finding.variable} to`}
                  value={null}
                  options={unmappedColumns.map((column) => ({ value: column, label: column }))}
                  onChange={(column: string) => onChange(renameVariable(doc, finding.variable, column))}
                />
              )}
              <DataQButton size="small" type="danger" onClick={() => onChange(removeRowsWithVariable(doc, finding.variable))}>
                Remove
              </DataQButton>
            </li>
          ))}
        </Section>
      )}

      {findings && findings.badCells.length > 0 && (
        <Section
          title={`Variables with a value their type can't hold (${findings.badCells.length})`}
          tone="warning"
          hint="One such value stops the whole variable importing."
        >
          {findings.badCells.map((finding) => {
            const row = doc.rows.find((r) => r.variable === finding.variable && r.type === finding.type)
            return (
              <li key={finding.variable} className="flex flex-wrap items-center gap-2">
                <span>
                  <span className="font-medium">{finding.label}</span> ({finding.type}): row {finding.row} of the data file has{" "}
                  {`"${finding.cell}"`}
                </span>
                {row && (
                  <DataQButton size="small" type="secondary" onClick={() => onChange(updateRow(doc, row.id, { type: "TEXT" }))}>
                    Make it TEXT
                  </DataQButton>
                )}
              </li>
            )
          })}
        </Section>
      )}

      {findings && nothingBlocks && (
        <Alert
          type="success"
          showIcon
          message={[
            // The data columns left out have their own line above, so this one doesn't repeat them.
            `Nothing in the datamap stops data importing (${plural(findings.rows, "row")}, ${plural(findings.columns, "column")}).`,
            checkIsStale && "Edited since the last check: check again to confirm.",
          ]
            .filter(Boolean)
            .join(" ")}
        />
      )}
    </div>
  )
}

export default DatamapFindings
